/*
 * Mobile product gallery (shipped from experiment 041 on 2026-11-02; desktop keeps the theme gallery).
 *
 * On screens up to 768px wide, product pages with two or more gallery images show an Instagram-style
 * carousel: full-bleed swipe, sliding dots, a "1/N" counter and previous/next arrows.
 *
 * The theme gallery stays in the DOM (hidden on mobile) and remains the source of truth. Its frame-image logic
 * (theme-print.min.js / theme.min.js) swaps the second slide for the selected frame's version of that scene,
 * using media alt text such as "FieryBlooms-Scene2-Black", and jumps to it. This gallery mirrors the slide list,
 * updates only the changed image, and follows that jump after a visitor changes the variant. Zoom is delegated
 * to the theme's fancybox slides.
 */
(function () {
  'use strict';

  var ACTIVE_CLASS = 'coa-gallery-active';
  var MOBILE_QUERY = '(max-width: 768px)';
  var DOTS_WINDOW = 5;
  var DOT_STEP = 10;
  var VARIANT_CHANGE_WINDOW = 2000;
  var THEME_READY_TIMEOUT = 1500;
  var FRAME_VALUES = ['black', 'blackframe', 'white', 'whiteframe', 'gold', 'goldframe', 'wood', 'woodframe',
    'lightwood', 'lightwoodframe', 'darkwood', 'darkwoodframe'];
  var IMAGE_WIDTHS = [360, 540, 720, 900, 1080, 1296, 1600, 2048];
  var CHEVRON = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function create(tag, className) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    return element;
  }

  function getThemeGallery() {
    var main = document.querySelector('.product__photos .product_main_photo_slider');
    if (!main) return null;
    var wrapper = main.querySelector('.swiper-wrapper');
    var photos = main.closest('.product__photos');
    var mount = photos && photos.parentElement;
    if (!wrapper || !photos || !mount) return null;
    return { wrapper: wrapper, photos: photos, mount: mount };
  }

  function getThemeSlides(theme) {
    return Array.prototype.filter.call(theme.wrapper.children, function (element) {
      return element.classList.contains('swiper-slide');
    });
  }

  // Shopify size suffix to width template, the theme's own URL scheme: name_800x.jpg -> name_{width}x.jpg.
  var SIZE_SUFFIX = /_\d+x\d*(\.[a-z0-9]+)(\?|$)/i;

  function widthTemplate(url) {
    if (!url) return '';
    if (url.indexOf('{width}') !== -1) return url;
    return SIZE_SUFFIX.test(url) ? url.replace(SIZE_SUFFIX, '_{width}x$1$2') : '';
  }

  function readSlide(slide) {
    var image = slide.querySelector('.product-image-main img') || slide.querySelector('img');
    var link = slide.querySelector('[data-fancybox]');
    var fallback = (link && link.getAttribute('href')) || '';
    // lazysizes rewrites an image's data-src and srcset once it is in view, which often happens to the first
    // slide before this runs, leaving neither src nor currentSrc on the hidden theme image. The zoom link's
    // server-rendered URL never changes, so it backs up the data-src template.
    var candidates = [
      image && image.getAttribute('data-src'),
      fallback,
      image && image.getAttribute('data-photoswipe-src'),
      image && (image.getAttribute('srcset') || '').split(',')[0].trim().split(' ')[0]
    ];
    var template = '';
    for (var i = 0; i < candidates.length && !template; i += 1) template = widthTemplate(candidates[i]);
    var src = '';
    var srcset = '';

    if (template) {
      src = template.replace('{width}', '1080');
      srcset = IMAGE_WIDTHS.map(function (width) {
        return template.replace('{width}', width) + ' ' + width + 'w';
      }).join(', ');
    } else if (image) {
      src = image.currentSrc || image.getAttribute('src') || fallback;
    }

    return {
      key: slide.getAttribute('data-mediaid') || src,
      framed: isFramedAlt(slide.getAttribute('data-mediaalt') || (image && image.getAttribute('alt')) || ''),
      src: src,
      srcset: srcset,
      fallback: fallback,
      ratio: parseFloat(image && image.getAttribute('data-aspectratio')) || 1,
      alt: image ? image.getAttribute('alt') || '' : '',
      zoomTarget: slide.querySelector('[data-fancybox]')
    };
  }

  // Same rule as the theme's getFrameFromAlt: the last "-" segment of the media alt names the frame.
  function isFramedAlt(alt) {
    var parts = String(alt || '').toLowerCase().split('-');
    var last = (parts[parts.length - 1] || '').replace(/[\s_()]/g, '');
    return FRAME_VALUES.indexOf(last) !== -1;
  }

  // Where the theme jumps after a frame change: to the swapped-in frame image, or to the first image otherwise.
  function frameJumpTarget(previous, items) {
    for (var i = 0; i < items.length; i += 1) {
      if (!previous[i] || previous[i].key !== items[i].key) return items[i].framed ? i : 0;
    }
    return null;
  }

  function clampRatio(ratio) {
    return Math.min(Math.max(ratio, 0.8), 1.91);
  }

  function buildImage(item, eager) {
    var image = create('img');
    if (item.srcset) {
      image.srcset = item.srcset;
      image.sizes = '100vw';
    }
    if (item.fallback && item.fallback !== item.src) {
      image.addEventListener('error', function retry() {
        image.removeEventListener('error', retry);
        image.removeAttribute('srcset');
        image.src = item.fallback;
      });
    }
    image.src = item.src || item.fallback;
    image.alt = item.alt;
    image.decoding = 'async';
    image.loading = eager ? 'eager' : 'lazy';
    image.draggable = false;
    return image;
  }

  function Gallery(theme) {
    this.theme = theme;
    this.items = [];
    this.keys = '';
    this.index = 0;
    this.dotStart = 0;
    this.cells = [];

    this.root = create('div', 'coa-gallery');
    this.root.setAttribute('data-coa-gallery', '');
    theme.mount.classList.add('coa-gallery-mount');
    theme.mount.insertBefore(this.root, theme.photos);

    var self = this;
    this.variantChangedAt = 0;
    document.addEventListener('variant:change', function () { self.variantChangedAt = Date.now(); });
  }

  Gallery.prototype.sync = function () {
    var slides = getThemeSlides(this.theme);
    var keys = slides.map(function (slide) {
      return slide.getAttribute('data-mediaid') || '';
    }).join('|');
    if (keys === this.keys) return;

    var firstSync = this.keys === '';
    var previous = this.items;
    var items = slides.map(readSlide);
    this.keys = keys;
    this.setItems(items);

    // Follow the theme's frame logic only after a visitor changed the variant (never on page load).
    if (firstSync || Date.now() - this.variantChangedAt > VARIANT_CHANGE_WINDOW) return;
    var target = frameJumpTarget(previous, items);
    if (target === null) return;
    this.index = target;
    this.goTo(target, true);
    this.updateIndicators();
  };

  // Update the gallery to a new slide list: only changed images are swapped when the count is unchanged.
  Gallery.prototype.setItems = function (items) {
    var previous = this.items;
    this.items = items;
    if (this.index >= items.length) this.index = 0;

    if (previous.length !== items.length || this.cells.length !== items.length) {
      this.render();
      return;
    }

    var self = this;
    items.forEach(function (item, position) {
      if (previous[position] && previous[position].key === item.key) return;
      self.swapImage(self.cells[position], item);
    });
  };

  Gallery.prototype.swapImage = function (cell, item) {
    var current = cell.querySelector('img');
    var image = buildImage(item, true);
    image.classList.add('coa-gallery-swap');
    if (current) cell.replaceChild(image, current); else cell.appendChild(image);
  };

  Gallery.prototype.observe = function () {
    var self = this;
    var timer = null;
    this.observer = new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { self.sync(); }, 60);
    });
    this.observer.observe(this.theme.wrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  };

  Gallery.prototype.render = function () {
    var self = this;
    var total = this.items.length;
    this.root.textContent = '';
    this.cells = [];
    this.track = null;
    this.counter = null;
    this.dots = [];
    this.dotsTrack = null;
    this.prevArrow = null;
    this.nextArrow = null;
    if (!total) return;

    var viewport = create('div', 'coa-gallery-viewport');
    var track = create('div', 'coa-gallery-track');
    var strip = create('div', 'coa-gallery-strip');

    viewport.style.setProperty('--coa-gallery-ratio', String(clampRatio(this.items[0].ratio)));
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', 'Product images');

    this.items.forEach(function (item, position) {
      var cell = create('button', 'coa-gallery-cell');
      cell.type = 'button';
      cell.setAttribute('aria-label', 'Zoom image ' + (position + 1) + ' of ' + total);
      cell.appendChild(buildImage(item, position < 2));
      cell.addEventListener('click', function () { self.openZoom(position); });
      strip.appendChild(cell);
    });

    track.appendChild(strip);
    viewport.appendChild(track);
    this.root.appendChild(viewport);

    this.track = track;
    this.cells = Array.prototype.slice.call(strip.children);

    if (total > 1) {
      var counter = create('div', 'coa-gallery-counter');
      counter.setAttribute('aria-hidden', 'true');
      viewport.appendChild(counter);
      this.counter = counter;

      var dotsWindow = create('div', 'coa-gallery-dots');
      var dotsTrack = create('div', 'coa-gallery-dots-track');
      dotsWindow.setAttribute('aria-hidden', 'true');
      dotsWindow.style.width = (Math.min(total, DOTS_WINDOW) * DOT_STEP - (DOT_STEP - 6)) + 'px';
      for (var i = 0; i < total; i += 1) {
        var dot = create('span', 'coa-gallery-dot');
        dotsTrack.appendChild(dot);
        this.dots.push(dot);
      }
      dotsWindow.appendChild(dotsTrack);
      this.root.appendChild(dotsWindow);
      this.dotsTrack = dotsTrack;

      this.prevArrow = this.buildArrow('prev', 'Previous image', -1);
      this.nextArrow = this.buildArrow('next', 'Next image', 1);
      viewport.appendChild(this.prevArrow);
      viewport.appendChild(this.nextArrow);
    }

    var frame = null;
    track.addEventListener('scroll', function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        var position = Math.max(0, Math.min(total - 1, Math.round(track.scrollLeft / self.stride())));
        if (position !== self.index) {
          self.index = position;
          self.updateIndicators();
        }
      });
    }, { passive: true });

    this.goTo(this.index, false);
    this.updateIndicators(true);
  };

  Gallery.prototype.buildArrow = function (direction, label, step) {
    var self = this;
    var arrow = create('button', 'coa-gallery-arrow coa-gallery-arrow--' + direction);
    arrow.type = 'button';
    arrow.setAttribute('aria-label', label);
    arrow.innerHTML = CHEVRON;
    arrow.addEventListener('click', function (event) {
      event.preventDefault();
      var target = Math.max(0, Math.min(self.items.length - 1, self.index + step));
      if (target !== self.index) self.goTo(target, true);
    });
    return arrow;
  };

  Gallery.prototype.stride = function () {
    if (this.cells.length > 1) {
      return (this.cells[1].offsetLeft - this.cells[0].offsetLeft) || this.track.clientWidth || 1;
    }
    return (this.track && this.track.clientWidth) || 1;
  };

  Gallery.prototype.goTo = function (position, smooth) {
    if (!this.track) return;
    var left = position * this.stride();
    if (typeof this.track.scrollTo === 'function') {
      this.track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      this.track.scrollLeft = left;
    }
  };

  /*
   * Instagram-style dots: at most five are visible. The active dot travels along the row; the row slides
   * only when the active dot would reach an edge while more images remain, and the edge dot shrinks to
   * show that the set continues.
   */
  Gallery.prototype.updateIndicators = function (instant) {
    var total = this.items.length;
    if (this.counter) this.counter.textContent = (this.index + 1) + '/' + total;
    if (this.prevArrow) this.prevArrow.classList.toggle('is-hidden', this.index === 0);
    if (this.nextArrow) this.nextArrow.classList.toggle('is-hidden', this.index === total - 1);
    if (!this.dots.length) return;

    var start = 0;
    if (total > DOTS_WINDOW) {
      start = this.dotStart;
      if (this.index > start + DOTS_WINDOW - 2) start = this.index - (DOTS_WINDOW - 2);
      if (this.index < start + 1) start = this.index - 1;
      start = Math.max(0, Math.min(start, total - DOTS_WINDOW));
    }
    this.dotStart = start;
    var end = Math.min(total, start + DOTS_WINDOW) - 1;

    if (this.dotsTrack) {
      if (instant) this.dotsTrack.classList.add('is-instant');
      this.dotsTrack.style.transform = 'translateX(' + (-start * DOT_STEP) + 'px)';
      if (instant) {
        var dotsTrack = this.dotsTrack;
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () { dotsTrack.classList.remove('is-instant'); });
        });
      }
    }

    for (var i = 0; i < this.dots.length; i += 1) {
      var dot = this.dots[i];
      var outside = i < start || i > end;
      var edge = (i === start && start > 0) || (i === end && end < total - 1);
      dot.classList.toggle('is-active', i === this.index);
      dot.classList.toggle('is-edge', !outside && edge);
      dot.classList.toggle('is-outside', outside);
    }
  };

  Gallery.prototype.openZoom = function (position) {
    var item = this.items[position];
    if (!item) return;
    var target = item.zoomTarget;
    if (!target || !target.isConnected) return;
    if (window.jQuery) {
      window.jQuery(target).trigger('click');
    } else {
      target.click();
    }
  };

  // Wait until the theme gallery has initialised and applied its frame filter, so the first render is final.
  function whenThemeReady(callback) {
    var started = Date.now();
    (function check() {
      var main = document.querySelector('.product_main_photo_slider');
      if (main && main.classList.contains('swiper-initialized')) {
        window.requestAnimationFrame(function () { window.requestAnimationFrame(callback); });
      } else if (Date.now() - started > THEME_READY_TIMEOUT) {
        callback();
      } else {
        window.setTimeout(check, 50);
      }
    })();
  }

  function mount() {
    whenThemeReady(function () {
      var theme = getThemeGallery();
      if (!theme || getThemeSlides(theme).length < 2 || theme.mount.querySelector('[data-coa-gallery]')) return;
      document.documentElement.classList.add(ACTIVE_CLASS);
      var gallery = new Gallery(theme);
      gallery.sync();
      gallery.observe();
    });
  }

  // Build only on mobile; a later switch to desktop width shows the theme gallery again through CSS.
  function start() {
    var root = document.documentElement;
    if (root.hasAttribute('data-coa-gallery-started')) return;
    root.setAttribute('data-coa-gallery-started', '');
    var mobile = window.matchMedia(MOBILE_QUERY);
    if (mobile.matches) {
      mount();
      return;
    }
    var onChange = function (event) {
      if (!event.matches) return;
      if (typeof mobile.removeEventListener === 'function') mobile.removeEventListener('change', onChange);
      else mobile.removeListener(onChange);
      mount();
    };
    if (typeof mobile.addEventListener === 'function') mobile.addEventListener('change', onChange);
    else mobile.addListener(onChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
